import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPostBySlug, getPublishedPosts } from '../services/blogService';

/* ═══════════════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════════════ */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

:root {
  --primary:#0A0F1E; --accent:#00D68F; --white:#ffffff;
  --text:#1a1f2e; --text-muted:#6B7280; --text-light:#9CA3AF;
  --border:#E5E7EB; --surface:#F9FAFB; --surface2:#F3F4F6;
  --radius:14px; --radius-sm:8px;
  --article-max: 680px;
  --serif: 'Instrument Serif', serif;
  --sans: 'DM Sans', sans-serif;
  --mono: 'JetBrains Mono', monospace;
}

*, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }

.bp { font-family:var(--sans); color:var(--text); background:var(--white); -webkit-font-smoothing:antialiased; }

/* ── Reading progress bar ── */
.bp-progress {
  position:fixed; top:0; left:0; right:0; height:3px; z-index:300;
  background:var(--border);
}
.bp-progress-fill {
  height:100%; background:linear-gradient(90deg,var(--accent),#00B8D9);
  transition:width 0.1s linear; border-radius:0 2px 2px 0;
}

/* ── Topbar ── */
.bp-topbar {
  position:fixed; top:3px; left:0; right:0; z-index:200; height:60px;
  display:flex; align-items:center; justify-content:space-between;
  padding:0 32px; transition:all 0.3s;
}
.bp-topbar.scrolled {
  background:rgba(255,255,255,0.95); backdrop-filter:blur(20px);
  border-bottom:1px solid var(--border); box-shadow:0 1px 16px rgba(0,0,0,0.04);
}
.bp-logo {
  display:flex; align-items:center; gap:10px; text-decoration:none;
  font-weight:700; font-size:1.1rem; color:var(--primary);
}
.bp-logo-icon {
  width:32px; height:32px; background:var(--primary); border-radius:6px;
  display:flex; align-items:center; justify-content:center;
  color:var(--accent); font-weight:700; font-size:1rem;
}
.bp-topbar-center {
  flex:1; text-align:center; padding:0 32px;
  opacity:0; transition:opacity 0.3s;
}
.bp-topbar.scrolled .bp-topbar-center { opacity:1; }
.bp-topbar-title {
  font-size:0.82rem; font-weight:600; color:var(--text);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:400px;
  margin:0 auto;
}
.bp-topbar-right { display:flex; align-items:center; gap:16px; }
.bp-topbar-right a {
  font-size:0.85rem; font-weight:500; color:var(--text-muted);
  text-decoration:none;
}
.bp-topbar-btn {
  background:var(--accent); color:var(--primary); font-weight:700;
  padding:8px 18px; border-radius:var(--radius-sm); font-size:0.82rem;
  text-decoration:none; white-space:nowrap;
}

/* ── Layout ── */
.bp-layout {
  display:grid;
  grid-template-columns:200px minmax(0,var(--article-max)) 220px;
  gap:0; max-width:1280px; margin:0 auto;
  padding:0 24px; padding-top:80px;
  justify-content:center;
}

/* ── Left sidebar (category / back) ── */
.bp-left {
  padding-top:60px; padding-right:40px;
  position:sticky; top:80px; height:fit-content;
}
.bp-back {
  display:inline-flex; align-items:center; gap:6px;
  font-size:0.82rem; font-weight:600; color:var(--text-muted);
  text-decoration:none; transition:color 0.2s; margin-bottom:32px;
  background:none; border:none; cursor:pointer; font-family:inherit;
}
.bp-back:hover { color:var(--primary); }
.bp-left-meta { display:flex; flex-direction:column; gap:20px; }
.bp-left-stat { }
.bp-left-stat-label {
  font-size:0.67rem; font-weight:700; text-transform:uppercase;
  letter-spacing:0.1em; color:var(--text-light); margin-bottom:5px;
}
.bp-left-stat-value { font-size:0.88rem; font-weight:600; color:var(--text); }

/* ── Right TOC ── */
.bp-toc {
  padding-top:60px; padding-left:40px;
  position:sticky; top:80px; height:fit-content; max-height:calc(100vh - 100px);
  overflow-y:auto;
}
.bp-toc-title {
  font-size:0.67rem; font-weight:700; text-transform:uppercase;
  letter-spacing:0.12em; color:var(--text-light); margin-bottom:14px;
}
.bp-toc-link {
  display:block; padding:5px 0 5px 12px;
  border-left:1.5px solid transparent;
  font-size:0.8rem; color:var(--text-muted); text-decoration:none;
  transition:all 0.15s; line-height:1.4; cursor:pointer;
  background:none; border-top:none; border-right:none; border-bottom:none;
  width:100%; text-align:left; font-family:inherit;
}
.bp-toc-link:hover { color:var(--primary); border-left-color:var(--border); }
.bp-toc-link.active { color:var(--accent); border-left-color:var(--accent); font-weight:600; }

/* ── Article ── */
.bp-article { padding:48px 0 120px; min-width:0; }

/* Hero */
.bp-hero { margin-bottom:56px; }
.bp-category-row { display:flex; align-items:center; gap:12px; margin-bottom:20px; }
.bp-category-badge {
  font-size:0.72rem; font-weight:700; text-transform:uppercase;
  letter-spacing:0.1em; padding:4px 12px; border-radius:100px;
}
.bp-read-time { font-size:0.8rem; color:var(--text-light); }
.bp-title {
  font-family:var(--serif); font-weight:400; font-size:2.9rem;
  line-height:1.15; letter-spacing:-0.02em; color:var(--primary);
  margin-bottom:20px;
}
.bp-subtitle {
  font-size:1.15rem; color:var(--text-muted); line-height:1.7;
  margin-bottom:32px; font-style:italic;
}
.bp-author-row {
  display:flex; align-items:center; gap:14px;
  padding:18px 0; border-top:1px solid var(--border); border-bottom:1px solid var(--border);
}
.bp-author-avatar {
  width:44px; height:44px; border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  font-weight:700; font-size:0.9rem; color:#fff; flex-shrink:0;
}
.bp-author-name { font-weight:700; font-size:0.92rem; color:var(--primary); }
.bp-author-role { font-size:0.78rem; color:var(--text-muted); }
.bp-author-date { margin-left:auto; font-size:0.8rem; color:var(--text-light); text-align:right; }

/* Cover image placeholder */
.bp-cover {
  width:100%; aspect-ratio:16/8; border-radius:var(--radius);
  margin:36px 0; overflow:hidden; position:relative;
  display:flex; align-items:center; justify-content:center;
}
.bp-cover-pattern {
  position:absolute; inset:0; opacity:0.06;
  background-image:repeating-linear-gradient(
    45deg, var(--primary) 0px, var(--primary) 1px,
    transparent 1px, transparent 20px
  );
}

/* ── Body typography ── */
.bp-body { font-size:1.02rem; line-height:1.85; color:#2d3748; }
.bp-body h2 {
  font-family:var(--serif); font-weight:400; font-size:1.9rem;
  color:var(--primary); margin:56px 0 20px; line-height:1.2;
  scroll-margin-top:90px;
}
.bp-body h3 {
  font-weight:700; font-size:1.15rem; color:var(--primary);
  margin:36px 0 14px; scroll-margin-top:90px;
}
.bp-body p { margin-bottom:24px; }
.bp-body strong { font-weight:700; color:var(--primary); }
.bp-body em { font-style:italic; color:var(--text-muted); }
.bp-body a { color:var(--accent); font-weight:500; text-decoration:none; }
.bp-body a:hover { text-decoration:underline; }
.bp-body ul, .bp-body ol { padding-left:24px; margin-bottom:24px; }
.bp-body li { margin-bottom:8px; line-height:1.7; }
.bp-body code {
  font-family:var(--mono); font-size:0.85em; background:var(--surface2);
  color:#9333ea; padding:2px 7px; border-radius:4px; border:1px solid var(--border);
}

/* Pull quote */
.bp-pullquote {
  border-left:4px solid var(--accent); padding:20px 28px;
  margin:36px 0; background:rgba(0,214,143,0.04); border-radius:0 var(--radius-sm) var(--radius-sm) 0;
}
.bp-pullquote p {
  font-family:var(--serif); font-size:1.35rem; line-height:1.5;
  color:var(--primary); font-style:italic; margin:0;
}

/* Callout box */
.bp-callout {
  border-radius:var(--radius-sm); padding:20px 22px;
  margin:28px 0; font-size:0.92rem; line-height:1.65;
  display:flex; gap:14px; align-items:flex-start;
}
.bp-callout-icon { font-size:1.2rem; flex-shrink:0; margin-top:1px; }
.bp-callout.info { background:rgba(59,130,246,0.06); border:1px solid rgba(59,130,246,0.2); color:#1e40af; }
.bp-callout.tip  { background:rgba(0,214,143,0.06); border:1px solid rgba(0,214,143,0.25); color:#065f46; }
.bp-callout.warn { background:rgba(245,158,11,0.06); border:1px solid rgba(245,158,11,0.25); color:#92400e; }

/* Table */
.bp-table-wrap { overflow-x:auto; margin:28px 0; border-radius:var(--radius-sm); border:1px solid var(--border); }
.bp-table { width:100%; border-collapse:collapse; font-size:0.88rem; }
.bp-table th {
  background:var(--surface); padding:12px 16px; text-align:left;
  font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.08em;
  color:var(--text-muted); border-bottom:1px solid var(--border);
}
.bp-table td { padding:12px 16px; border-bottom:1px solid var(--border); vertical-align:top; }
.bp-table tr:last-child td { border-bottom:none; }
.bp-table tr:hover td { background:var(--surface); }
.bp-table td:first-child { font-weight:600; color:var(--primary); }
.bp-table .best { color:var(--accent); font-weight:700; }

/* Key number highlight */
.bp-stat-row {
  display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin:32px 0;
}
.bp-stat-card {
  background:var(--surface); border:1px solid var(--border);
  border-radius:var(--radius-sm); padding:20px; text-align:center;
}
.bp-stat-value { font-family:var(--serif); font-size:2.2rem; color:var(--accent); font-weight:400; }
.bp-stat-label { font-size:0.78rem; color:var(--text-muted); margin-top:4px; line-height:1.4; }

/* Tags */
.bp-tags { display:flex; gap:8px; flex-wrap:wrap; margin:48px 0 32px; }
.bp-tag {
  font-size:0.75rem; font-weight:600; padding:5px 12px;
  border-radius:100px; background:var(--surface); border:1px solid var(--border);
  color:var(--text-muted); cursor:pointer; transition:all 0.2s; text-decoration:none;
}
.bp-tag:hover { border-color:var(--accent); color:var(--accent); }

/* Share */
.bp-share {
  display:flex; align-items:center; gap:12px; padding:24px 0;
  border-top:1px solid var(--border); border-bottom:1px solid var(--border); margin-bottom:64px;
}
.bp-share-label { font-size:0.82rem; font-weight:700; color:var(--text-muted); margin-right:4px; }
.bp-share-btn {
  display:inline-flex; align-items:center; gap:7px;
  padding:8px 16px; border-radius:var(--radius-sm); font-size:0.8rem; font-weight:600;
  text-decoration:none; border:1px solid var(--border); background:#fff;
  color:var(--text); cursor:pointer; font-family:inherit; transition:all 0.2s;
}
.bp-share-btn:hover { border-color:var(--primary); background:var(--surface); }

/* Author card */
.bp-author-card {
  display:flex; gap:20px; padding:32px; border-radius:var(--radius);
  background:var(--surface); border:1px solid var(--border); margin-bottom:64px;
  align-items:flex-start;
}
.bp-author-card-avatar {
  width:64px; height:64px; border-radius:50%; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  font-weight:700; font-size:1.2rem; color:#fff;
}
.bp-author-card-name { font-weight:700; font-size:1rem; color:var(--primary); margin-bottom:4px; }
.bp-author-card-role { font-size:0.8rem; color:var(--accent); font-weight:600; margin-bottom:10px; }
.bp-author-card-bio { font-size:0.88rem; color:var(--text-muted); line-height:1.6; }

/* Related posts */
.bp-related-title {
  font-family:var(--serif); font-size:1.6rem; color:var(--primary);
  margin-bottom:24px;
}
.bp-related-grid {
  display:grid; grid-template-columns:repeat(2,1fr); gap:20px;
}
.bp-related-card {
  border-radius:var(--radius); border:1px solid var(--border);
  text-decoration:none; color:inherit; overflow:hidden; display:block;
  transition:box-shadow 0.25s, transform 0.25s;
}
.bp-related-card:hover { box-shadow:0 8px 32px rgba(0,0,0,0.1); transform:translateY(-3px); }
.bp-related-thumb {
  height:120px; display:flex; align-items:center; justify-content:center;
  position:relative; overflow:hidden;
}
.bp-related-body { padding:18px; }
.bp-related-cat { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:8px; }
.bp-related-title-sm { font-weight:700; font-size:0.92rem; line-height:1.35; color:var(--primary); }
.bp-related-meta { font-size:0.75rem; color:var(--text-light); margin-top:8px; }

/* Newsletter CTA */
.bp-newsletter {
  background:var(--primary); border-radius:var(--radius);
  padding:40px 36px; margin:64px 0; text-align:center;
}
.bp-newsletter h3 { font-family:var(--serif); font-size:1.7rem; color:#fff; margin-bottom:8px; }
.bp-newsletter p { color:rgba(255,255,255,0.5); font-size:0.92rem; margin-bottom:24px; }
.bp-newsletter-form { display:flex; gap:10px; max-width:400px; margin:0 auto; }
.bp-newsletter-input {
  flex:1; padding:12px 16px; border-radius:var(--radius-sm);
  border:1.5px solid rgba(255,255,255,0.12); background:rgba(255,255,255,0.06);
  color:#fff; font-family:inherit; font-size:0.9rem; outline:none;
}
.bp-newsletter-input::placeholder { color:rgba(255,255,255,0.3); }
.bp-newsletter-input:focus { border-color:var(--accent); }
.bp-newsletter-btn {
  padding:12px 22px; border-radius:var(--radius-sm);
  background:var(--accent); color:var(--primary); font-weight:700;
  border:none; cursor:pointer; font-family:inherit; font-size:0.9rem; white-space:nowrap;
}

/* Mobile */
@media(max-width:1100px) {
  .bp-layout { grid-template-columns:minmax(0,var(--article-max)); justify-content:center; }
  .bp-left, .bp-toc { display:none; }
  .bp-title { font-size:2.2rem; }
}
@media(max-width:640px) {
  /* layout */
  .bp-layout  { grid-template-columns:1fr; padding:0 16px; padding-top:70px; }
  .bp-article { padding:32px 0 80px; }

  /* tipografia */
  .bp-title    { font-size:1.75rem; line-height:1.2; }
  .bp-subtitle { font-size:1rem; line-height:1.6; }
  .bp-body     { font-size:0.97rem; line-height:1.75; }
  .bp-body h2  { font-size:1.45rem; margin:40px 0 16px; }
  .bp-body h3  { font-size:1.05rem; margin:28px 0 12px; }
  .bp-pullquote { font-size:1.05rem; padding:20px 16px; margin:24px 0; }

  /* hero */
  .bp-category-row { flex-wrap:wrap; gap:8px; }
  .bp-author-date  { display:none; }

  /* tabela — scroll horizontal */
  .bp-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .bp-table      { min-width:420px; }

  /* stats */
  .bp-stat-row  { grid-template-columns:1fr; }
  .bp-stat-card { padding:20px 16px; }

  /* callouts */
  .bp-callout { padding:14px 16px; }

  /* relacionados */
  .bp-related-grid { grid-template-columns:1fr; }

  /* newsletter */
  .bp-newsletter-form        { flex-direction:column; }
  .bp-newsletter-form input  { width:100%; }
  .bp-newsletter-form button { width:100%; }

  /* compartilhar */
  .bp-share { flex-wrap:wrap; gap:8px; }

  /* topbar */
  .bp-topbar        { padding:0 16px; }
  .bp-topbar-center { display:none; }

  /* author card */
  .bp-author-card        { flex-direction:column; text-align:center; }
  .bp-author-card-avatar { margin:0 auto; }
}

@media(max-width:380px) {
  .bp-title   { font-size:1.5rem; }
  .bp-body    { font-size:0.93rem; }
  .bp-body h2 { font-size:1.25rem; }
}
`;

/* ═══════════════════════════════════════════════════════════════
   ARTIGOS
═══════════════════════════════════════════════════════════════ */
const ARTIGOS = {
  'sac-ou-price': {
    slug: 'sac-ou-price',
    category: 'Crédito Imobiliário',
    categoryCor: '#3B82F6',
    title: 'SAC ou Price: qual sistema de amortização é melhor para o seu cliente?',
    subtitle: 'Entenda a diferença entre os dois sistemas, quando usar cada um e como apresentar a comparação de forma clara para fechar mais negócios.',
    date: '20 jan 2026',
    readTime: '8 min',
    author: { name: 'David Oliveira', role: 'CEO — SimulaBank', ini: 'DO', cor: '#3B82F6' },
    tags: ['SAC', 'Price', 'Amortização', 'Financiamento', 'Correspondente'],
    toc: [
      { id: 's1', label: 'O que é amortização?' },
      { id: 's2', label: 'Sistema SAC' },
      { id: 's3', label: 'Sistema Price' },
      { id: 's4', label: 'Comparativo prático' },
      { id: 's5', label: 'Qual indicar ao cliente?' },
      { id: 's6', label: 'Como apresentar a simulação' },
      { id: 's7', label: 'Conclusão' },
    ],
    coverGradient: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #0A0F1E 100%)',
    coverEmoji: '🏦',
  },
  'simulacao-em-2-minutos': {
    slug: 'simulacao-em-2-minutos',
    category: 'Ferramentas',
    categoryCor: '#8B5CF6',
    title: 'Como apresentar a simulação de financiamento em 2 minutos',
    subtitle: 'Script e metodologia para transformar a simulação em uma apresentação convincente para o cliente. Pare de perder negócios por apresentações confusas.',
    date: '14 jan 2026',
    readTime: '5 min',
    author: { name: 'Carlos Mendes', role: 'Head de Produto — SimulaBank', ini: 'CM', cor: '#14B8A6' },
    tags: ['Vendas', 'Apresentação', 'Script', 'Correspondente', 'SimulaBank'],
    toc: [
      { id: 's1', label: 'O problema da apresentação longa' },
      { id: 's2', label: 'A metodologia dos 2 minutos' },
      { id: 's3', label: 'O script completo' },
      { id: 's4', label: 'Usando o SimulaBank' },
      { id: 's5', label: 'Dicas finais' },
    ],
    coverGradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #0A0F1E 100%)',
    coverEmoji: '⚡',
  },
  'o-que-e-cet': {
    slug: 'o-que-e-cet',
    category: 'CET',
    categoryCor: '#F59E0B',
    title: 'O que é o CET e por que ele importa mais que a taxa de juros',
    subtitle: 'Custo Efetivo Total inclui seguros, tarifas e todos os encargos do financiamento. Aprenda a usá-lo como seu principal argumento de venda.',
    date: '30 dez 2025',
    readTime: '7 min',
    author: { name: 'Ana Martins', role: 'CTO — SimulaBank', ini: 'AM', cor: '#8B5CF6' },
    tags: ['CET', 'Custo Efetivo Total', 'Juros', 'MIP', 'DFI', 'Transparência'],
    toc: [
      { id: 's1', label: 'O que é o CET?' },
      { id: 's2', label: 'O que compõe o CET' },
      { id: 's3', label: 'CET vs taxa de juros' },
      { id: 's4', label: 'Exemplo prático' },
      { id: 's5', label: 'Como usar o CET como argumento' },
    ],
    coverGradient: 'linear-gradient(135deg, #78350f 0%, #b45309 50%, #0A0F1E 100%)',
    coverEmoji: '📊',
  },
};

const RELATED_POSTS = [
  { slug:'sac-ou-price', category:'Crédito Imobiliário', categoryCor:'#3B82F6', title:'SAC ou Price: qual sistema de amortização é melhor?', date:'20 jan 2026', readTime:'8 min', coverGradient:'linear-gradient(135deg,#1e3a8a,#0A0F1E)' },
  { slug:'simulacao-em-2-minutos', category:'Ferramentas', categoryCor:'#8B5CF6', title:'Como apresentar a simulação em 2 minutos', date:'14 jan 2026', readTime:'5 min', coverGradient:'linear-gradient(135deg,#4c1d95,#0A0F1E)' },
  { slug:'o-que-e-cet', category:'CET', categoryCor:'#F59E0B', title:'O que é o CET e por que importa mais que a taxa', date:'30 dez 2025', readTime:'7 min', coverGradient:'linear-gradient(135deg,#78350f,#0A0F1E)' },
  { slug:'mip-dfi', category:'MIP/DFI', categoryCor:'#00D68F', title:'Seguro MIP e DFI: o que são e como calculá-los', date:'15 dez 2025', readTime:'9 min', coverGradient:'linear-gradient(135deg,#065f46,#0A0F1E)' },
];

/* ═══════════════════════════════════════════════════════════════
   CONTEÚDOS DOS ARTIGOS
═══════════════════════════════════════════════════════════════ */
function ContentSacOuPrice() {
  return (
    <>
      <section id="s1">
        <h2>O que é amortização?</h2>
        <p>Amortização é o processo de pagamento gradual de uma dívida ao longo do tempo. Em um financiamento imobiliário, cada parcela é composta por três elementos principais: <strong>amortização do principal</strong> (devolução do que foi emprestado), <strong>juros</strong> (remuneração do banco pelo empréstimo) e <strong>seguros</strong> (MIP e DFI, obrigatórios por lei).</p>
        <p>A forma como a amortização é distribuída ao longo do prazo define o <strong>sistema de amortização</strong>. No Brasil, os dois sistemas mais usados em crédito imobiliário são o <strong>SAC</strong> e o <strong>Price</strong> (também chamado de Tabela Price).</p>
        <div className="bp-callout info">
          <span className="bp-callout-icon">ℹ️</span>
          <div>A <strong>Caixa Econômica Federal</strong> é a única que ainda oferece o sistema Price em larga escala. Os demais grandes bancos (BRB, Santander, Itaú, Inter, Bradesco) operam predominantemente com SAC.</div>
        </div>
      </section>

      <section id="s2">
        <h2>Sistema SAC — Amortização Constante</h2>
        <p>No SAC (<em>Sistema de Amortização Constante</em>), a parcela de amortização do principal é <strong>fixa durante todo o prazo</strong>. Como os juros são calculados sobre o saldo devedor (que diminui mês a mês), o valor dos juros cai progressivamente — e consequentemente, <strong>as parcelas diminuem ao longo do tempo</strong>.</p>

        <div className="bp-stat-row">
          <div className="bp-stat-card">
            <div className="bp-stat-value">↓</div>
            <div className="bp-stat-label">Parcelas decrescentes<br/>ao longo do tempo</div>
          </div>
          <div className="bp-stat-card">
            <div className="bp-stat-value" style={{fontSize:'1.5rem'}}>Mais<br/>alto</div>
            <div className="bp-stat-label">Primeira parcela<br/>vs. Price</div>
          </div>
          <div className="bp-stat-card">
            <div className="bp-stat-value" style={{color:'var(--accent)'}}>↓</div>
            <div className="bp-stat-label">Menor custo total<br/>no longo prazo</div>
          </div>
        </div>

        <h3>Como funciona na prática</h3>
        <p>Para um financiamento de <strong>R$ 400.000</strong> em 360 meses (30 anos) a uma taxa de 0,85% a.m.:</p>
        <ul>
          <li>Amortização mensal fixa: R$ 400.000 ÷ 360 = <strong>R$ 1.111,11</strong></li>
          <li>Juros do mês 1: R$ 400.000 × 0,85% = <strong>R$ 3.400,00</strong></li>
          <li>Primeira parcela (sem seguros): <strong>R$ 4.511,11</strong></li>
          <li>Última parcela (sem seguros): <strong>R$ 1.120,56</strong> (amortização + juros mínimos)</li>
        </ul>

        <div className="bp-pullquote">
          <p>"No SAC, o mutuário paga mais no início e ganha liberdade financeira no final. É o sistema mais usado e recomendado por permitir maior quitação antecipada."</p>
        </div>
      </section>

      <section id="s3">
        <h2>Sistema Price — Parcela Constante</h2>
        <p>Na Tabela Price, as parcelas são <strong>iguais durante todo o prazo</strong>. Isso é possível porque a proporção entre amortização e juros muda mês a mês: no início, a maior parte da parcela vai para juros; no final, a maior parte vai para amortização.</p>
        <p>Este sistema é matematicamente mais complexo e, apesar de oferecer a <strong>parcela inicial menor</strong>, resulta em um <strong>custo total maior</strong> porque o saldo devedor diminui mais lentamente.</p>

        <div className="bp-callout warn">
          <span className="bp-callout-icon">⚠️</span>
          <div><strong>Atenção ao "efeito amortecedor":</strong> Nos primeiros anos do Price, uma parte tão pequena da parcela vai para o principal que qualquer correção monetária (como TR) pode fazer o saldo devedor <em>aumentar</em> ao invés de diminuir. Isso é chamado de amortização negativa.</div>
        </div>
      </section>

      <section id="s4">
        <h2>Comparativo prático</h2>
        <p>Usando os mesmos parâmetros (R$ 400k, 360 meses, 0,85% a.m.):</p>

        <div className="bp-table-wrap">
          <table className="bp-table">
            <thead>
              <tr><th>Indicador</th><th>SAC</th><th>Price</th></tr>
            </thead>
            <tbody>
              <tr><td>1ª parcela</td><td>R$ 4.631,11</td><td className="best">R$ 3.842,17 ✓</td></tr>
              <tr><td>Última parcela</td><td className="best">R$ 1.240,56 ✓</td><td>R$ 3.842,17</td></tr>
              <tr><td>Total pago em juros</td><td className="best">R$ 692.401 ✓</td><td>R$ 982.381</td></tr>
              <tr><td>Custo total</td><td className="best">R$ 1.092.401 ✓</td><td>R$ 1.382.381</td></tr>
              <tr><td>Saldo devedor (5 anos)</td><td className="best">R$ 333.333 ✓</td><td>R$ 378.102</td></tr>
            </tbody>
          </table>
        </div>

        <p>A diferença de custo total entre os dois sistemas neste exemplo é de quase <strong>R$ 290.000</strong> — praticamente o valor de um imóvel.</p>
      </section>

      <section id="s5">
        <h2>Qual indicar ao cliente?</h2>
        <p>A resposta depende do perfil financeiro de cada cliente. Como regra geral:</p>

        <h3>Indique o SAC quando:</h3>
        <ul>
          <li>O cliente consegue suportar a parcela inicial mais alta</li>
          <li>Há intenção de quitar antecipadamente (saldo devedor cai mais rápido)</li>
          <li>O prazo é longo (acima de 180 meses)</li>
          <li>Renda tem tendência de crescimento ao longo do tempo</li>
        </ul>

        <h3>Considere o Price quando:</h3>
        <ul>
          <li>O cliente está no limite de comprometimento de renda (a parcela inicial menor pode ser decisiva para aprovação)</li>
          <li>O prazo é curto (até 60–80 meses, onde a diferença de custo é menor)</li>
          <li>O cliente tem certeza de que não vai quitar antecipadamente</li>
        </ul>

        <div className="bp-callout tip">
          <span className="bp-callout-icon">💡</span>
          <div><strong>Dica prática:</strong> Sempre simule os dois e mostre o comparativo ao cliente. A transparência gera confiança e reduz objeções pós-fechamento.</div>
        </div>
      </section>

      <section id="s6">
        <h2>Como apresentar a simulação com o SimulaBank</h2>
        <p>O SimulaBank gera automaticamente a simulação SAC para todos os 6 bancos em segundos. No PDF gerado você encontra:</p>
        <ul>
          <li>Tabela comparativa com primeira e última parcela de cada banco</li>
          <li>Custo total (principal + juros + seguros)</li>
          <li>CET anual de cada banco</li>
          <li>Indicação automática do melhor banco para o perfil do cliente</li>
          <li>Tabela completa de parcelas mês a mês (sob demanda)</li>
        </ul>
        <p>Com esse material em mãos, a apresentação ao cliente fica objetiva: <strong>em 2 minutos você explica o que custará e qual banco oferece a melhor condição</strong>.</p>
      </section>

      <section id="s7">
        <h2>Conclusão</h2>
        <p>Para a grande maioria dos clientes com capacidade de pagamento, o <strong>SAC é a melhor escolha</strong> pelo menor custo total ao longo do tempo. O Price pode ser útil para perfis com renda justa, mas exige uma explicação clara sobre o risco de amortização negativa em contratos com TR.</p>
        <p>O mais importante é que o cliente entenda o que está assinando. Apresentar os dois cenários com um comparativo visual — como o gerado pelo SimulaBank — é a forma mais eficiente de transformar essa complexidade em uma decisão clara e confiante.</p>
      </section>
    </>
  );
}

function ContentSimulacao2Min() {
  return (
    <>
      <section id="s1">
        <h2>O problema da apresentação longa</h2>
        <p>A maioria dos correspondentes bancários perde negócios não por falta de conhecimento técnico, mas por <strong>apresentações confusas e longas</strong>. O cliente se perde em números, fica inseguro e adia a decisão.</p>
        <p>A solução não é simplificar a análise — é <strong>estruturar a apresentação</strong>.</p>
        <div className="bp-stat-row">
          <div className="bp-stat-card"><div className="bp-stat-value">30min</div><div className="bp-stat-label">Tempo médio de apresentação<br/>sem metodologia</div></div>
          <div className="bp-stat-card"><div className="bp-stat-value" style={{color:'var(--accent)'}}>2min</div><div className="bp-stat-label">Com o método e<br/>SimulaBank</div></div>
          <div className="bp-stat-card"><div className="bp-stat-value">+40%</div><div className="bp-stat-label">Taxa de fechamento<br/>reportada por usuários</div></div>
        </div>
      </section>

      <section id="s2">
        <h2>A metodologia dos 2 minutos</h2>
        <p>A apresentação eficaz de uma simulação segue 4 etapas sequenciais:</p>
        <ol>
          <li><strong>Contextualizar</strong> (15 seg): confirmar os dados do cliente</li>
          <li><strong>Revelar o resultado</strong> (30 seg): mostrar o melhor banco primeiro</li>
          <li><strong>Comparar</strong> (45 seg): mostrar a diferença em relação aos outros</li>
          <li><strong>Fechar</strong> (30 seg): propor o próximo passo</li>
        </ol>
        <div className="bp-pullquote">
          <p>"Não mostre os 6 bancos de uma vez. Comece pelo melhor e deixe o cliente curioso sobre os outros."</p>
        </div>
      </section>

      <section id="s3">
        <h2>O script completo</h2>
        <p>Este script foi testado com mais de 200 correspondentes e pode ser adaptado ao seu estilo:</p>

        <div className="bp-callout tip">
          <span className="bp-callout-icon">💬</span>
          <div>
            <strong>Abertura (15 seg):</strong><br/>
            <em>"João, aqui está a simulação para o seu financiamento de R$ 400 mil, em 30 anos, com entrada de R$ 100 mil. Comparei os 6 maiores bancos do Brasil para você."</em>
          </div>
        </div>

        <div className="bp-callout tip">
          <span className="bp-callout-icon">🏆</span>
          <div>
            <strong>Revelação (30 seg):</strong><br/>
            <em>"A melhor condição hoje é o Banco Inter: primeira parcela de R$ 3.842, e ao longo de 30 anos você paga R$ 290 mil a menos do que no pior banco. [PAUSA] Só com essa escolha você economiza o valor de um carro popular."</em>
          </div>
        </div>

        <div className="bp-callout tip">
          <span className="bp-callout-icon">📊</span>
          <div>
            <strong>Comparação (45 seg):</strong><br/>
            <em>"Veja o PDF: cada linha é um banco, ordenado do melhor para o pior. A coluna azul é o custo total — a diferença entre o Inter e o Itaú é de R$ 287 mil. Ou seja: banco certo, menos R$ 287 mil no bolso em 30 anos."</em>
          </div>
        </div>

        <div className="bp-callout tip">
          <span className="bp-callout-icon">✅</span>
          <div>
            <strong>Fechamento (30 seg):</strong><br/>
            <em>"Posso dar entrada na proposta do Inter agora. Você tem os documentos básicos? Leva uns 5 minutos para submeter. Qual e-mail te mando a cópia do PDF?"</em>
          </div>
        </div>
      </section>

      <section id="s4">
        <h2>Usando o SimulaBank na apresentação</h2>
        <p>O SimulaBank foi desenhado para suportar exatamente esse fluxo:</p>
        <ul>
          <li><strong>Simulação em segundos:</strong> enquanto conversa com o cliente, a comparação já aparece na tela</li>
          <li><strong>PDF white-label:</strong> gere o relatório com sua logo e envie no WhatsApp do cliente antes de desligar</li>
          <li><strong>Melhor opção destacada:</strong> o sistema indica automaticamente o banco mais vantajoso</li>
          <li><strong>CET visível:</strong> o Custo Efetivo Total de cada banco fica explícito, sem surpresas</li>
        </ul>
      </section>

      <section id="s5">
        <h2>Dicas finais</h2>
        <ul>
          <li>Nunca mencione todos os bancos pelo nome no verbal — deixe o PDF falar. Você cita apenas o melhor e o pior.</li>
          <li>Use números concretos, não percentuais. "R$ 290 mil a menos" impacta mais que "CET de 10,89%".</li>
          <li>Envie o PDF pelo WhatsApp antes de encerrar a ligação. Cliente com material em mãos fecha mais.</li>
          <li>Se o cliente hesitar, volte à economia total: "O que você faria com R$ 290 mil?"</li>
        </ul>
      </section>
    </>
  );
}

function ContentCET() {
  return (
    <>
      <section id="s1">
        <h2>O que é o CET?</h2>
        <p>O <strong>CET — Custo Efetivo Total</strong> é a taxa anual que representa o verdadeiro custo de um financiamento. Diferente da taxa de juros nominal (que mede apenas os juros), o CET inclui <strong>todos os encargos</strong> embutidos na operação.</p>
        <p>Criado pelo Banco Central do Brasil e regulamentado pela Resolução CMN 3.517/2007, o CET é de divulgação obrigatória por todas as instituições financeiras em propostas de crédito.</p>
        <div className="bp-callout info">
          <span className="bp-callout-icon">📜</span>
          <div>O CET foi criado justamente porque a taxa nominal pode ser enganosa. Um banco com taxa de 0,82% a.m. pode ter CET maior que um banco com taxa de 0,89% a.m., dependendo das tarifas e seguros cobrados.</div>
        </div>
      </section>

      <section id="s2">
        <h2>O que compõe o CET no financiamento imobiliário</h2>
        <p>Para financiamentos imobiliários, o CET é composto principalmente por:</p>

        <div className="bp-table-wrap">
          <table className="bp-table">
            <thead><tr><th>Componente</th><th>Impacto típico</th><th>Observação</th></tr></thead>
            <tbody>
              <tr><td>Taxa de juros</td><td>Maior componente (~85%)</td><td>Varia por banco e perfil</td></tr>
              <tr><td>Seguro MIP</td><td>2–8% do CET</td><td>Aumenta com a idade do mutuário</td></tr>
              <tr><td>Seguro DFI</td><td>~1% do CET</td><td>Proporcional ao valor do imóvel</td></tr>
              <tr><td>Tarifas de avaliação</td><td>Diluído no prazo</td><td>Cobrado uma vez na contratação</td></tr>
              <tr><td>Tarifa de abertura</td><td>Varia por banco</td><td>Alguns bancos não cobram</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="s3">
        <h2>CET vs taxa de juros — a armadilha mais comum</h2>
        <p>Muitos clientes — e até alguns profissionais — comparam financiamentos apenas pela taxa de juros mensal. Isso pode levar a uma escolha equivocada.</p>

        <div className="bp-pullquote">
          <p>"Comparar financiamentos só pela taxa de juros é como comparar passagens aéreas só pelo preço do assento — ignorando as taxas de bagagem, embarque e cancelamento."</p>
        </div>

        <p>Um banco pode oferecer taxa de 0,82% a.m. mas cobrar seguros muito mais caros que um concorrente com taxa de 0,87% a.m. No final, o CET do banco "mais barato" pode ser maior.</p>
      </section>

      <section id="s4">
        <h2>Exemplo prático</h2>
        <p>Financiamento de R$ 400.000, 360 meses, mutuário de 35 anos:</p>

        <div className="bp-table-wrap">
          <table className="bp-table">
            <thead><tr><th>Banco</th><th>Taxa a.m.</th><th>MIP mensal</th><th>1ª Parcela</th><th>CET a.a.</th></tr></thead>
            <tbody>
              <tr><td>Inter</td><td>0,85%</td><td>R$ 120</td><td>R$ 3.842</td><td className="best">10,89% ✓</td></tr>
              <tr><td>BRB</td><td>0,82%</td><td>R$ 186</td><td>R$ 4.017</td><td>11,12%</td></tr>
              <tr><td>Caixa</td><td>0,87%</td><td>R$ 98</td><td>R$ 4.120</td><td>11,34%</td></tr>
              <tr><td>Bradesco</td><td>0,92%</td><td>R$ 110</td><td>R$ 4.510</td><td>12,01%</td></tr>
              <tr><td>Santander</td><td>0,89%</td><td>R$ 140</td><td>R$ 4.698</td><td>12,30%</td></tr>
              <tr><td>Itaú</td><td>0,95%</td><td>R$ 124</td><td>R$ 5.024</td><td>12,89%</td></tr>
            </tbody>
          </table>
        </div>

        <p>Repare que o BRB tem a <strong>menor taxa nominal</strong> (0,82%), mas o <strong>Inter tem o menor CET</strong> (10,89%) por conta dos seguros mais baratos. Usar apenas a taxa como critério levaria o cliente a escolher o BRB — pagando mais no total.</p>
      </section>

      <section id="s5">
        <h2>Como usar o CET como argumento de venda</h2>
        <p>O CET é seu melhor aliado para gerar transparência e confiança. Use assim:</p>
        <ul>
          <li><strong>Mostre o CET lado a lado</strong> com a taxa de juros no comparativo. O cliente percebe que você fez uma análise completa, não apenas a superficial.</li>
          <li><strong>Explique em linguagem simples:</strong> "O CET é o que você realmente paga ao banco por ano, incluindo tudo — juros, seguros, taxas. É o número que o Banco Central obriga o banco a mostrar."</li>
          <li><strong>Use para justificar a indicação:</strong> "Indico o Inter não por ter a menor taxa, mas por ter o menor CET — que é o que determina quanto você vai gastar no total."</li>
        </ul>
        <div className="bp-callout tip">
          <span className="bp-callout-icon">💡</span>
          <div>O SimulaBank calcula o CET automaticamente para todos os bancos simulados, seguindo a metodologia do Banco Central. Você não precisa calcular manualmente.</div>
        </div>
      </section>
    </>
  );
}

const CONTENT_MAP = {
  'sac-ou-price': ContentSacOuPrice,
  'simulacao-em-2-minutos': ContentSimulacao2Min,
  'o-que-e-cet': ContentCET,
};

/* ═══════════════════════════════════════════════════════════════
   MARKDOWN RENDERER (for Firebase posts)
═══════════════════════════════════════════════════════════════ */
function MarkdownBody({ content }) {
  if (!content) return null;
  let html = content
    .replace(/^### (.+)$/gm, '<h3 id="$1">$1</h3>')
    .replace(/^## (.+)$/gm, (_, t) => `<h2 id="${t.toLowerCase().replace(/\s+/g,'-').replace(/[^\w-]/g,'')}">${t}</h2>`)
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\> (.+)$/gm, '<blockquote class="bp-pullquote"><p>$1</p></blockquote>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  html = `<p>${html}</p>`;
  // wrap consecutive <li> in <ul>
  html = html.replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>');
  return <div className="bp-body" dangerouslySetInnerHTML={{ __html: html }} />;
}

function extractToc(content) {
  if (!content) return [];
  const matches = [...content.matchAll(/^## (.+)$/gm)];
  return matches.map((m, i) => ({
    id: m[1].toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, ''),
    label: m[1],
  }));
}

/* ═══════════════════════════════════════════════════════════════
   PAGE COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Firebase post state
  const [firebasePost, setFirebasePost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  // Load from Firebase first, fallback to hardcoded
  useEffect(() => {
    let cancelled = false;
    setLoadingPost(true);
    (async () => {
      try {
        const post = await getPostBySlug(slug);
        if (!cancelled) setFirebasePost(post);
        // Load related
        const all = await getPublishedPosts();
        if (!cancelled) setRelatedPosts(all.filter(p => p.slug !== slug).slice(0, 2));
      } catch (e) {
        console.warn('Blog Firebase error, using hardcoded:', e);
      }
      if (!cancelled) setLoadingPost(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Determine data source
  const hardcoded = ARTIGOS[slug];
  const isFirebase = !!firebasePost;
  const artigo = isFirebase ? firebasePost : (hardcoded || ARTIGOS['sac-ou-price']);
  const Content = isFirebase ? null : (CONTENT_MAP[slug] || ContentSacOuPrice);
  const toc = isFirebase ? extractToc(firebasePost.content) : artigo.toc;

  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(toc[0]?.id);
  const [newsletterSent, setNewsletterSent] = useState(false);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);

  // Scroll tracking
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      // Progress
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // TOC active section
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: '-64px 0px -60% 0px', threshold: 0 }
    );
    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [slug, toc]);

  // Scroll to top on slug change
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const related = isFirebase
    ? relatedPosts
    : RELATED_POSTS.filter(p => p.slug !== artigo.slug).slice(0, 2);

  const catStyle = {
    background: artigo.categoryCor + '18',
    color: artigo.categoryCor,
    border: `1px solid ${artigo.categoryCor}30`,
  };

  return (
    <div className="bp">
      <style>{css}</style>

      {loadingPost && !hardcoded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{
              width: 40, height: 40, border: '3px solid var(--border)',
              borderTopColor: 'var(--accent)', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
            }} />
            Carregando artigo...
          </div>
        </div>
      )}

      {(!loadingPost || hardcoded) && (
      <>
      {/* ── Progress bar ── */}
      <div className="bp-progress">
        <div className="bp-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* ── Topbar ── */}
      <header className={`bp-topbar${scrolled ? ' scrolled' : ''}`}>
        <Link to="/" className="bp-logo">
          <div className="bp-logo-icon">S</div>
          SimulaBank
        </Link>
        <div className="bp-topbar-center">
          <div className="bp-topbar-title">{artigo.title}</div>
        </div>
        <div className="bp-topbar-right">
          <Link to="/blog">← Blog</Link>
          <Link to="/cadastro" className="bp-topbar-btn">Testar grátis</Link>
        </div>
      </header>

      {/* ── Layout ── */}
      <div className="bp-layout">

        {/* Left sidebar */}
        <aside className="bp-left">
          <button className="bp-back" onClick={() => navigate('/blog')}>← Blog</button>
          <div className="bp-left-meta">
            <div className="bp-left-stat">
              <div className="bp-left-stat-label">Categoria</div>
              <div className="bp-left-stat-value" style={{ color: artigo.categoryCor }}>{artigo.category}</div>
            </div>
            <div className="bp-left-stat">
              <div className="bp-left-stat-label">Publicado em</div>
              <div className="bp-left-stat-value">{artigo.date}</div>
            </div>
            <div className="bp-left-stat">
              <div className="bp-left-stat-label">Leitura</div>
              <div className="bp-left-stat-value">{artigo.readTime}</div>
            </div>
            <div className="bp-left-stat">
              <div className="bp-left-stat-label">Progresso</div>
              <div style={{ marginTop: 6 }}>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: 'var(--accent)', borderRadius: 2, transition: 'width 0.2s' }} />
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', marginTop: 4 }}>{Math.round(progress)}% lido</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Article */}
        <article className="bp-article">

          {/* Hero */}
          <header className="bp-hero">
            <div className="bp-category-row">
              <span className="bp-category-badge" style={catStyle}>{artigo.category}</span>
              <span className="bp-read-time">⏱ {artigo.readTime} de leitura</span>
            </div>

            <h1 className="bp-title">{artigo.title}</h1>
            <p className="bp-subtitle">{artigo.subtitle}</p>

            <div className="bp-author-row">
              <div className="bp-author-avatar" style={{ background: artigo.author.cor }}>
                {artigo.author.ini}
              </div>
              <div>
                <div className="bp-author-name">{artigo.author.name}</div>
                <div className="bp-author-role">{artigo.author.role}</div>
              </div>
              <div className="bp-author-date">
                {artigo.date}<br />
                <span style={{ fontSize: '0.72rem' }}>{artigo.readTime} de leitura</span>
              </div>
            </div>
          </header>

          {/* Cover */}
          <div className="bp-cover" style={{ background: artigo.coverGradient }}>
            <div className="bp-cover-pattern" />
            <span style={{ fontSize: '4rem', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.3))' }}>
              {artigo.coverEmoji}
            </span>
          </div>

          {/* Body */}
          {isFirebase ? (
            <MarkdownBody content={firebasePost.content} />
          ) : (
            <div className="bp-body">
              <Content />
            </div>
          )}

          {/* Tags */}
          <div className="bp-tags">
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginRight: 4 }}>Tags:</span>
            {artigo.tags.map(t => (
              <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="bp-tag">{t}</Link>
            ))}
          </div>

          {/* Share */}
          <div className="bp-share">
            <span className="bp-share-label">Compartilhar:</span>
            <a className="bp-share-btn" href={`https://wa.me/?text=${encodeURIComponent(artigo.title + ' — ' + window.location.href)}`} target="_blank" rel="noreferrer">
              <span>💬</span> WhatsApp
            </a>
            <a className="bp-share-btn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer">
              <span>💼</span> LinkedIn
            </a>
            <button className="bp-share-btn" onClick={copyLink}>
              <span>{copied ? '✓' : '🔗'}</span> {copied ? 'Copiado!' : 'Copiar link'}
            </button>
          </div>

          {/* Author card */}
          <div className="bp-author-card">
            <div className="bp-author-card-avatar" style={{ background: artigo.author.cor }}>
              {artigo.author.ini}
            </div>
            <div>
              <div className="bp-author-card-name">{artigo.author.name}</div>
              <div className="bp-author-card-role">{artigo.author.role}</div>
              <div className="bp-author-card-bio">
                Especialista em financiamento imobiliário e tecnologia para o mercado de crédito.
                Escreve sobre estratégias práticas para correspondentes bancários, assessorias e imobiliárias.
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="bp-newsletter">
            <h3>Toda sexta na sua caixa de entrada</h3>
            <p>Dicas práticas e dados do mercado imobiliário para fechar mais contratos.</p>
            {newsletterSent ? (
              <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1rem' }}>
                ✓ Inscrito com sucesso! Te vejo na sexta.
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); setNewsletterSent(true); }} className="bp-newsletter-form">
                <input
                  type="email" required placeholder="seu@email.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  className="bp-newsletter-input"
                />
                <button type="submit" className="bp-newsletter-btn">Inscrever-se</button>
              </form>
            )}
          </div>

          {/* Related posts */}
          <section>
            <h2 className="bp-related-title">Continue lendo</h2>
            <div className="bp-related-grid">
              {related.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="bp-related-card">
                  <div className="bp-related-thumb" style={{ background: p.coverGradient }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.15)' }} />
                  </div>
                  <div className="bp-related-body">
                    <div className="bp-related-cat" style={{ color: p.categoryCor }}>{p.category}</div>
                    <div className="bp-related-title-sm">{p.title}</div>
                    <div className="bp-related-meta">{p.date} · {p.readTime} de leitura</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

        </article>

        {/* Right TOC */}
        <aside className="bp-toc">
          <div className="bp-toc-title">Neste artigo</div>
          {toc.map(item => (
            <button
              key={item.id}
              className={`bp-toc-link${activeSection === item.id ? ' active' : ''}`}
              onClick={() => scrollTo(item.id)}
            >
              {item.label}
            </button>
          ))}

          <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
            <div className="bp-toc-title">Testar grátis</div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
              Compare financiamentos de 6 bancos em segundos. Sem cartão de crédito.
            </p>
            <Link to={`https://wa.me/5561991826667?text=${encodeURIComponent('Começar grátis')}`} target="_blank" rel="noopener noreferrer" style={{
              display: 'block', textAlign: 'center', padding: '10px 14px',
              background: 'var(--accent)', color: 'var(--primary)', borderRadius: 'var(--radius-sm)',
              textDecoration: 'none', fontWeight: 700, fontSize: '0.82rem',
            }}>
              Começar grátis →
            </Link>
          </div>
        </aside>

      </div>
      </>
      )}
    </div>
  );
}